/**
 * Article Prices Report Page
 *
 * Paginado en el servidor, igual que Costos: el export pide todas las filas
 * solo al tocarlo.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Download, Search, Tag } from 'lucide-react'

import api from '@/lib/api'
import { formatCurrency, debounce } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'
import { Button } from '@/components/ui/button'
import { Pagination } from '@/components/ui/pagination'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface ArticlePrice {
  id: number
  code: string
  alternative_code: string | null
  description: string
  sale_price_1: number
  sale_price_2: number
  sale_price_3: number
  sale_price_ivi_1: number
  sale_price_ivi_2: number
  sale_price_ivi_3: number
  family_name: string | null
  provider_name: string | null
  stock: number
}

interface ArticlePricesResponse {
  items: ArticlePrice[]
  total: number
  page: number
  page_size: number
  pages: number
}

type SortOrder = 'code' | 'name_asc' | 'name_desc' | 'price_desc' | 'price_asc'

const PAGE_SIZE = 50

const SORT_PARAMS: Record<SortOrder, [string, string]> = {
  code: ['code', 'asc'],
  name_asc: ['name', 'asc'],
  name_desc: ['name', 'desc'],
  price_desc: ['price', 'desc'],
  price_asc: ['price', 'asc'],
}

export function ArticlePricesReport() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [familyFilter, setFamilyFilter] = useState<string>('all')
  const [providerFilter, setProviderFilter] = useState<string>('all')
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOrder, setSortOrder] = useState<SortOrder>('code')
  const [page, setPage] = useState(1)

  const { data: families } = useQuery({
    queryKey: ['families-all'],
    queryFn: async () => {
      // Lista completa para el dropdown; /catalogs/families pagina a 20.
      const response = await api.get('/articles/catalogs/families')
      return response.data as { id: number; name: string }[]
    },
  })

  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: async () => {
      const response = await api.get('/articles/catalogs/providers')
      return response.data as { id: number; name: string }[]
    },
  })

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
      setPage(1)
    }, 300),
    []
  )

  useEffect(() => {
    setPage(1)
  }, [familyFilter, providerFilter, sortOrder])

  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams()
    if (searchQuery.trim()) params.set('search', searchQuery.trim())
    if (familyFilter !== 'all') params.set('family_id', familyFilter)
    if (providerFilter !== 'all') params.set('provider_id', providerFilter)
    const [sortBy, sortDir] = SORT_PARAMS[sortOrder]
    params.set('sort_by', sortBy)
    params.set('sort_dir', sortDir)
    return params
  }, [searchQuery, familyFilter, providerFilter, sortOrder])

  const { data, isLoading } = useQuery({
    queryKey: ['reports-article-prices', page, searchQuery, familyFilter, providerFilter, sortOrder],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('page', page.toString())
      params.set('page_size', PAGE_SIZE.toString())
      const response = await api.get(`/reports/articles/prices?${params}`)
      return response.data as ArticlePricesResponse
    },
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.pages ?? 0

  const [exportRequested, setExportRequested] = useState(false)
  const { data: exportData, isFetching: isExporting } = useQuery({
    queryKey: ['reports-article-prices-export', searchQuery, familyFilter, providerFilter, sortOrder],
    queryFn: async () => {
      const params = buildFilterParams()
      params.set('all', 'true')
      const response = await api.get(`/reports/articles/prices?${params}`)
      return response.data as ArticlePricesResponse
    },
    enabled: exportRequested,
    staleTime: 60_000,
  })
  const exportItems = exportData?.items ?? []

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/reports')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold">{t('reports.articlePrices')}</h1>
          <p className="text-sm text-muted-foreground">{t('reports.articlePricesSubtitle')}</p>
        </div>
        {exportItems.length > 0 ? (
          <ExportButtons
            data={exportItems}
            filename="precios-articulos"
            columns={[
              { key: 'code', header: t('articles.code'), asText: true },
              { key: 'alternative_code', header: t('articles.alternativeCode'), format: (v) => (v ? String(v) : ''), asText: true },
              { key: 'description', header: t('articles.description') },
              { key: 'provider_name', header: t('articles.provider'), format: (v) => (v ? String(v) : '') },
              { key: 'stock', header: t('articles.stock'), format: (v) => String(v) },
              { key: 'sale_price_ivi_1', header: t('articles.price1'), format: (v) => formatCurrency(v as number) },
              { key: 'sale_price_ivi_2', header: t('articles.price2'), format: (v) => formatCurrency(v as number) },
              { key: 'sale_price_ivi_3', header: t('articles.price3'), format: (v) => formatCurrency(v as number) },
            ]}
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="h-10 shrink-0"
            onClick={() => setExportRequested(true)}
            disabled={isExporting || total === 0}
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">
              {isExporting ? t('common.loading') : t('export.export')}
            </span>
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
        <Select value={familyFilter} onValueChange={setFamilyFilter}>
          <SelectTrigger>
            <SelectValue placeholder={t('reports.allFamilies')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('reports.allFamilies')}</SelectItem>
            {families?.map((f) => (
              <SelectItem key={f.id} value={String(f.id)}>
                {f.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <SearchableSelect
          options={[
            { value: 'all', label: t('reports.allProviders') },
            ...(providers?.map((p) => ({ value: String(p.id), label: p.name })) ?? []),
          ]}
          value={providerFilter}
          onValueChange={(value) => setProviderFilter(value || 'all')}
          placeholder={t('reports.allProviders')}
        />

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('articles.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value)
              debouncedSearch(e.target.value)
            }}
            className="pl-9"
          />
        </div>
      </div>

      {/* List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Tag className="h-4 w-4 text-purple-500" />
              {t('reports.priceList')} ({t('reports.articlesCount', { count: total })})
            </CardTitle>
            <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as SortOrder)}>
              <SelectTrigger className="h-9 w-[160px] shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="code">{t('reports.sortCode')}</SelectItem>
                <SelectItem value="name_asc">{t('reports.sortNameAsc')}</SelectItem>
                <SelectItem value="name_desc">{t('reports.sortNameDesc')}</SelectItem>
                <SelectItem value="price_desc">{t('reports.sortPriceDesc')}</SelectItem>
                <SelectItem value="price_asc">{t('reports.sortPriceAsc')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : items.length > 0 ? (
            <div className="space-y-2">
              {items.map((article) => (
                <div
                  key={article.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 active:bg-muted cursor-pointer touch-manipulation"
                  onClick={() => navigate(`/articles/${article.id}`)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">
                      {article.code}
                      {article.alternative_code && <span className="text-muted-foreground"> / {article.alternative_code}</span>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {article.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {article.provider_name || '-'} • {t('articles.stock')}: {article.stock}
                    </p>
                  </div>
                  <div className="text-right shrink-0 text-xs space-y-1">
                    <p>
                      <span className="text-muted-foreground">{t('articles.price1')}:</span>{' '}
                      <span className="font-medium">{formatCurrency(article.sale_price_ivi_1)}</span>
                    </p>
                    <p>
                      <span className="text-muted-foreground">{t('articles.price2')}:</span>{' '}
                      <span className="font-medium">{formatCurrency(article.sale_price_ivi_2)}</span>
                    </p>
                    <p>
                      <span className="text-muted-foreground">{t('articles.price3')}:</span>{' '}
                      <span className="font-medium">{formatCurrency(article.sale_price_ivi_3)}</span>
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-center py-8">
              {t('reports.noDataAvailable')}
            </p>
          )}

          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {page} {t('common.of')} {totalPages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
